/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, yupToFormErrors, Field as FormikField } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import ValidationSchema from './EditFormValidation'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Modal from '../../../../components/Modal'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'

const HalfWidthDiv = styled.div`
  width: 50%;
  padding-right: 23px;
  float: left;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

const RadioGroup = styled.div`
  margin-top: 10px;
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  tag: any
  loading?: Promise<any> | null
}

const EditForm: React.FC<EditFormProps> = ({ onSubmit, doClose, isEdit, tag, loading }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()

  const getDefaults = (vals: any) => {
    const defaults = validationSchema.cast(vals, {
      stripUnknown: true,
      context: vals
    })
    return defaults
  }

  const [initialValues, setInitialValues] = useState(() => getDefaults(tag))

  useEffect(() => {
    // Update initial values when tag data is loaded
    if (isEdit && tag && Object.keys(tag).length > 0) {
      setInitialValues(getDefaults(tag))
    }
  }, [tag, isEdit])

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  const isReadOnly = isEdit && tag?.type === 'REGULATORY_SUBMISSION'

  return (
    <div>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={(values, actions) => {
          const finalData = getDefaults(values)
          const tagData = cloneDeep(finalData)
          return onSubmit(tagData, actions)
        }}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <div>
            <Loader blocking={(isEdit && Object.keys(tag).length === 0) || loading !== null}>
              <Modal.Body>
                <form className={'clearfix'}>
                  <HalfWidthDiv>
                    <StyledFormGroup controlId="22a43d3c-07c9-45d1-99ab-f8ffcaccf2b5">
                      <Form.Label>{translate('NAME')}</Form.Label>
                      <Field
                        type="text"
                        name="name"
                        maxLength={500}
                        autoFocus
                        disabled={isReadOnly}
                        id="22a43d3c-07c9-45d1-99ab-f8ffcaccf2b5"
                      />
                      <ErrorMessageStyled name="name" />
                    </StyledFormGroup>

                    <StyledFormGroup controlId="049b23f3-d69f-48b5-967c-25ec328f4657">
                      <Form.Label>{translate('TAG_TYPE')}</Form.Label>
                      <RadioGroup>
                        <div className="radio">
                          <label htmlFor={`152dd862-7739-4ab1-b320-b655d50961ca-NON_ETMF`}>
                            <FormikField
                              type="radio"
                              name="type"
                              value="NON_ETMF"
                              disabled={isEdit}
                              id={`152dd862-7739-4ab1-b320-b655d50961ca-NON_ETMF`}
                            />
                            {translate('SETTINGS_TAG_TYPE_NON_ETMF')}
                          </label>
                        </div>
                        <div className="radio">
                          <label htmlFor={`152dd862-7739-4ab1-b320-b655d50961ca-ETMF`}>
                            <FormikField
                              type="radio"
                              name="type"
                              value="ETMF"
                              disabled={isEdit}
                              id={`152dd862-7739-4ab1-b320-b655d50961ca-ETMF`}
                            />
                            {translate('SETTINGS_TAG_TYPE_ETMF')}
                          </label>
                        </div>
                        {isEdit && (
                          <div className="radio">
                            <label htmlFor={`152dd862-7739-4ab1-b320-b655d50961ca-REGULATORY_SUBMISSION`}>
                              <FormikField
                                type="radio"
                                name="type"
                                value="REGULATORY_SUBMISSION"
                                disabled={isEdit}
                                id={`152dd862-7739-4ab1-b320-b655d50961ca-REGULATORY_SUBMISSION`}
                              />
                              {translate('SETTINGS_TAG_TYPE_REGULATORY_SUBMISSION')}
                            </label>
                          </div>
                        )}
                      </RadioGroup>
                    </StyledFormGroup>
                  </HalfWidthDiv>
                </form>
              </Modal.Body>
              <Modal.Footer>
                {!isReadOnly && (
                  <>
                    <button
                      data-action={'cancel'}
                      className="btn btn-chita btn-link"
                      onClick={closeDialog}
                      id="4d3655e7-5c5e-4645-901f-1c1c4838e580"
                    >
                      {translate('CANCEL')}
                    </button>
                    <button
                      data-action={'save'}
                      className="btn btn-primary text-capitalize"
                      onClick={() => handleSubmit()}
                      id="66a5bac5-5918-4601-990c-ab1dccd0be91"
                    >
                      {translate('SAVE')}
                    </button>
                  </>
                )}
                {isReadOnly && (
                  <button
                    data-action={'ok'}
                    className="btn btn-primary"
                    onClick={closeDialog}
                    id="4d59e213-28c3-49ab-96c0-ca391c419605"
                  >
                    {translate('OK')}
                  </button>
                )}
              </Modal.Footer>
            </Loader>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default EditForm
